import { supabase } from "@/integrations/supabase/client";

/**
 * Check if a user has an active subscription
 * @param userId - The profile ID (not auth user ID)
 * @returns Promise<boolean> - True if user has active subscription
 */
export async function checkActiveSubscription(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .eq("status", "active")
      .gt("end_at", new Date().toISOString())
      .maybeSingle();

    if (error) {
      console.error("Error checking subscription:", error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error("Error in checkActiveSubscription:", error);
    return false;
  }
}

/**
 * Get the user's profile ID from their auth user ID
 * @returns Promise<string | null> - The profile ID or null
 */
export async function getUserProfileId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: profile, error } = await supabase
      .from("profiles")
      .select("id")
      .eq("auth_user_id", user.id)
      .single();

    if (error) {
      console.error("Error fetching profile:", error);
      return null;
    }

    return profile?.id || null;
  } catch (error) {
    console.error("Error in getUserProfileId:", error);
    return null;
  }
}

/**
 * Check if the current authenticated user has an active subscription
 * @returns Promise<boolean> - True if user has active subscription
 */
export async function checkCurrentUserSubscription(): Promise<boolean> {
  const profileId = await getUserProfileId();
  if (!profileId) return false;
  
  return await checkActiveSubscription(profileId);
}
